import { isAdminPosition, readUserPosition } from "../access/positions";
// Admin view for the Picking module.
//
// Three tabs map to a status-filter on /admin/picking/waves:
//   Active  → status IN (active, building)
//   Paused  → status = paused
//   History → status IN (completed, released)
//
// Click a row to open the detail panel (lines + audit events).
// SuperAdmin sees a "Generate test wave" button that POSTs to
// /admin/picking/wave/generate — used for QA seeding before the
// picker UI ships in PR-3.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Link2 } from "lucide-react";
import { useTranslation } from "../i18n";
import { apiFetch } from "../api/apiFetch";
import { useReadOnly } from "../access/ReadOnly";
import { useWarehouseScope } from "../access/useWarehouseScope";
import { useAccess } from "../access/AccessContext";
import { waveTypeLabelKey } from "./picking/selectorShared";

type WaveStatus = "building" | "active" | "paused" | "completed" | "released";
type PickType = "light" | "pick_and_sort" | "large";
type Tier = "high" | "low";

interface WaveRow {
    wave_id: number;
    warehouse: string;
    pick_type: PickType;
    // Putwall vs bay for a Pick & Sort wave; null/absent on older rows.
    sort_station?: "pick_and_sort" | "put_wall" | null;
    tier: Tier;
    status: WaveStatus;
    current_picker_uid: number | null;
    current_picker_name: string | null;
    alloc_method: "fifo" | "fastest";
    started_at: string | null;
    paused_at: string | null;
    completed_at: string | null;
    released_at: string | null;
    last_activity_at: string;
    created_at: string;
    orders_count: string;
    lines_count: string;
    lines_picked: string;
    // Paired wave (migration 223): which half, and who is on the other.
    sort_group_id?: number | null;
    segment_no?: number;
    segment_count?: number;
    awaiting_picker?: boolean;
    sibling_picker_name?: string | null;
    sibling_wave_id?: number | null;
}

interface WaveDetail {
    wave: WaveRow & { max_walk_order_at_build: number | null };
    orders: Array<{ order_id: string; tier: Tier; is_anchor: boolean; status: string }>;
    lines: Array<{
        line_id: number;
        sku: string;
        location: string;
        walk_order: number;
        qty_required: number;
        qty_picked: number;
        status: string;
        sort_index: number;
        parent_line_id: number | null;
        distribution: Array<{ order_id: string; qty: number }>;
    }>;
    events: Array<{
        event_id: number;
        ts: string;
        picker_uid: number | null;
        picker_name: string | null;
        action_type: string;
        sku: string | null;
        location: string | null;
        tote_id: string | null;
        barcode: string | null;
        qty: number | null;
        metadata: unknown;
    }>;
}

type Tab = "active" | "paused" | "history";
const STATUS_FOR_TAB: Record<Tab, WaveStatus[]> = {
    active:  ["building", "active"],
    paused:  ["paused"],
    history: ["completed", "released"],
};

const POLL_MS = 5000;

const formatTs = (raw: string | null) => {
    if (!raw) return "—";
    try {
        return new Date(raw).toLocaleString();
    } catch {
        return raw;
    }
};

const PickingAdmin = () => {
    const { t } = useTranslation();
    const readOnly = useReadOnly();
    const [tab, setTab] = useState<Tab>("active");
    const warehouseScope = useWarehouseScope();
    const [pickedWarehouse, setWarehouseFilter] = useState<string>("");
    // Without cross_warehouse the list is pinned to the person's own warehouse.
    const warehouseFilter = !warehouseScope.canCross && warehouseScope.own ? warehouseScope.own : pickedWarehouse;
    const [pickTypeFilter, setPickTypeFilter] = useState<PickType | "">("");
    const [tierFilter, setTierFilter] = useState<Tier | "">("");
    const [allWarehouses, setWarehouses] = useState<string[]>([]);
    const warehouses = warehouseScope.limit(allWarehouses);
    const [waves, setWaves] = useState<WaveRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedWaveId, setSelectedWaveId] = useState<number | null>(null);
    const [detail, setDetail] = useState<WaveDetail | null>(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [releasing, setReleasing] = useState(false);
    const [genWarehouse, setGenWarehouse] = useState<string>("");
    useEffect(() => {
        if (!warehouseScope.canCross && warehouseScope.own && genWarehouse !== warehouseScope.own) setGenWarehouse(warehouseScope.own);
    }, [warehouseScope.canCross, warehouseScope.own, genWarehouse]);
    const [genPickType, setGenPickType] = useState<PickType>("pick_and_sort");
    const [genTier, setGenTier] = useState<Tier>("low");

    // "Generate test wave" and "Force release" follow the matrix row admin.picking.test_waves
    // (Admin by default, 2026-10-02); before the access map settles, the old Admin-only rule.
    const { authoritative: accessAuthoritative, canWrite } = useAccess();
    const isSuperAdmin = accessAuthoritative ? canWrite("admin.picking.test_waves") : isAdminPosition(readUserPosition());

    // Load warehouses for the filter + generator dropdowns
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await apiFetch("/settings", {});
                if (!res.ok || !alive) return;
                const data = await res.json();
                if (alive && Array.isArray(data)) {
                    const names = data.map((r: { warehouse: string }) => r.warehouse).sort();
                    setWarehouses(names);
                    if (!genWarehouse && names.length > 0) setGenWarehouse(names[0]);
                }
            } catch { /* leave empty */ }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadWaves = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("status", STATUS_FOR_TAB[tab].join(","));
            if (warehouseFilter) params.set("warehouse", warehouseFilter);
            if (pickTypeFilter)  params.set("pickType",  pickTypeFilter);
            if (tierFilter)      params.set("tier",      tierFilter);
            const res = await apiFetch(`/admin/picking/waves?${params.toString()}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: WaveRow[] = await res.json();
            setWaves(data);
        } catch {
            toast.error(t("server_error"), { toastId: "picking-admin-load-err" });
        } finally {
            setLoading(false);
        }
    }, [tab, warehouseFilter, pickTypeFilter, tierFilter, t]);

    // Fetch on tab/filter change + poll while mounted.
    useEffect(() => {
        let alive = true;
        const tick = async () => { if (alive) await loadWaves(); };
        tick();
        const id = setInterval(tick, POLL_MS);
        return () => { alive = false; clearInterval(id); };
    }, [loadWaves]);

    const openDetail = async (waveId: number) => {
        setSelectedWaveId(waveId);
        setDetail(null);
        setDetailLoading(true);
        try {
            const res = await apiFetch(`/admin/picking/waves/${waveId}`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data: WaveDetail = await res.json();
            setDetail(data);
        } catch {
            toast.error(t("server_error"), { toastId: `picking-admin-detail-err-${waveId}` });
            setSelectedWaveId(null);
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetail = () => {
        setSelectedWaveId(null);
        setDetail(null);
    };

    const forceRelease = async (waveId: number) => {
        if (releasing) return;
        // Confirm-before-act: force-release rolls back live state.
        // window.confirm is the same gate used by the Danger Zone.
        const ok = window.confirm(t("picking_admin_force_release_confirm", { waveId: String(waveId) }));
        if (!ok) return;
        setReleasing(true);
        try {
            const res = await apiFetch(`/admin/picking/wave/${waveId}/release`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({}),
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                const reason = errBody?.reason ?? errBody?.message ?? `HTTP ${res.status}`;
                toast.error(t("picking_admin_force_release_failed", { reason }), { toastId: `picking-release-fail-${waveId}` });
                return;
            }
            const body = await res.json();
            toast.success(
                t("picking_admin_force_release_ok", {
                    waveId: String(waveId),
                    n: String(body.allocations_released ?? 0),
                }),
                { toastId: `picking-release-ok-${waveId}` },
            );
            setSelectedWaveId(null);
            setDetail(null);
            await loadWaves();
        } catch {
            toast.error(t("picking_admin_force_release_failed", { reason: "network" }), { toastId: `picking-release-net-${waveId}` });
        } finally {
            setReleasing(false);
        }
    };

    const generateWave = async () => {
        if (!genWarehouse) {
            toast.error(t("picking_admin_pick_warehouse"));
            return;
        }
        setGenerating(true);
        try {
            const res = await apiFetch("/admin/picking/wave/generate", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ warehouse: genWarehouse, pickType: genPickType, tier: genTier }),
            });
            if (!res.ok) {
                const errBody = await res.json().catch(() => ({}));
                const reason = errBody.code ?? errBody.message ?? `HTTP ${res.status}`;
                toast.error(t("picking_admin_generate_failed", { reason }), { toastId: "picking-gen-err" });
                return;
            }
            const summary = await res.json();
            toast.success(
                t("picking_admin_generate_ok", {
                    waveId: String(summary.wave_id),
                    orders: String(summary.orders_count),
                    lines: String(summary.lines_count),
                }),
                { toastId: "picking-gen-ok" },
            );
            await loadWaves();
        } catch {
            toast.error(t("server_error"), { toastId: "picking-gen-net-err" });
        } finally {
            setGenerating(false);
        }
    };

    const renderRow = (w: WaveRow) => (
        <tr key={w.wave_id} onClick={() => openDetail(w.wave_id)} style={{ cursor: "pointer" }}>
            <td>#{w.wave_id}</td>
            <td>{w.warehouse}</td>
            <td>
                {t(waveTypeLabelKey(w.pick_type, w.sort_station))}
                {(w.segment_count ?? 1) > 1 && (
                    <>
                        <span className="picking-admin-joined"data-testid={`picking-joined-${w.wave_id}`}>
                            <Link2 size={11} />
                            {t("picking_joined_badge", { no: String(w.segment_no ?? 1), of: String(w.segment_count) })}
                        </span>
                        {w.sibling_wave_id != null && (
                            <span className="picking-admin-segment">{t("picking_joined_with", { wave: String(w.sibling_wave_id) })}</span>
                        )}
                        {w.awaiting_picker && <span className="picking-admin-segment"> · {t("picking_segment_sibling_waiting")}</span>}
                    </>
                )}
            </td>
            <td>{t(w.tier === "high" ? "picking_tier_high" : "picking_tier_low")}</td>
            <td>{t(`picking_admin_status_${w.status}`)}</td>
            <td>
                {w.current_picker_name ?? "—"}
                {(w.segment_count ?? 1) > 1 && w.sibling_picker_name && (
                    <span className="picking-admin-segment"> + {w.sibling_picker_name}</span>
                )}
            </td>
            <td>{w.lines_picked} / {w.lines_count}</td>
            <td>{w.orders_count}</td>
            <td>{formatTs(w.created_at)}</td>
        </tr>
    );

    return (
        <div>
            {/* Tabs */}
            <div className="admin-settings-section" style={{ marginBottom: 16 }}>
                <div role="tablist" style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                    {(["active", "paused", "history"] as Tab[]).map(key => (
                        <button
                            key={key}
                            type="button"
                            role="tab"
                            aria-selected={tab === key}
                            className={`stock-alloc-pill${tab === key ? " stock-alloc-pill--active" : ""}`}
                            onClick={() => setTab(key)}
                        >
                            {t(`picking_admin_${key}`)}
                        </button>
                    ))}
                    <div style={{ flex: 1 }} />
                    <select
                        value={pickTypeFilter}
                        onChange={e => setPickTypeFilter(e.target.value as PickType | "")}
                        className="admin-settings-po-input"
                    >
                        <option value="">{t("picking_admin_filter_all_types")}</option>
                        <option value="light">{t("picking_pick_type_light")}</option>
                        <option value="pick_and_sort">{t("picking_pick_type_pick_and_sort")}</option>
                        <option value="large">{t("picking_pick_type_large")}</option>
                    </select>
                    <select
                        value={tierFilter}
                        onChange={e => setTierFilter(e.target.value as Tier | "")}
                        className="admin-settings-po-input"
                    >
                        <option value="">{t("picking_admin_filter_all_tiers")}</option>
                        <option value="low">{t("picking_tier_low")}</option>
                        <option value="high">{t("picking_tier_high")}</option>
                    </select>
                    <select
                        value={warehouseFilter}
                        onChange={e => setWarehouseFilter(e.target.value)}
                        disabled={!warehouseScope.canCross}
                        className="admin-settings-po-input"
                        style={{ minWidth: 160 }}
                    >
                        {warehouseScope.canCross && <option value="">{t("picking_admin_all_warehouses")}</option>}
                        {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                    </select>
                </div>

                {/* Generator (superAdmin only) */}
                {isSuperAdmin && (
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                        <strong style={{ marginRight: 8 }}>{t("picking_admin_generate")}</strong>
                        <select
                            value={genWarehouse}
                            onChange={e => setGenWarehouse(e.target.value)}
                            disabled={!warehouseScope.canCross}
                            className="admin-settings-po-input"
                        >
                            {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                        </select>
                        <select
                            value={genPickType}
                            onChange={e => setGenPickType(e.target.value as PickType)}
                            className="admin-settings-po-input"
                        >
                            <option value="pick_and_sort">{t("picking_pick_type_pick_and_sort")}</option>
                            <option value="light">{t("picking_pick_type_light")}</option>
                            <option value="large">{t("picking_pick_type_large")}</option>
                        </select>
                        <select
                            value={genTier}
                            onChange={e => setGenTier(e.target.value as Tier)}
                            className="admin-settings-po-input"
                        >
                            <option value="low">{t("picking_tier_low")}</option>
                            <option value="high">{t("picking_tier_high")}</option>
                        </select>
                        <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={generateWave}
                            disabled={generating || readOnly}
                            aria-disabled={readOnly}
                            title={readOnly ? t("read_only_mode") : undefined}
                        >
                            {generating ? t("saving") : t("picking_admin_generate_btn")}
                        </button>
                    </div>
                )}

                {/* List */}
                {loading && waves.length === 0 ? (
                    <div className="admin-settings-empty">{t("loading")}</div>
                ) : waves.length === 0 ? (
                    <div className="admin-settings-empty">{t(`picking_admin_no_${tab}`)}</div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                                    <th>{t("picking_wave_id_col")}</th>
                                    <th>{t("warehouse_col")}</th>
                                    <th>{t("picking_admin_pick_type_col")}</th>
                                    <th>{t("picking_tier")}</th>
                                    <th>{t("picking_admin_status_col")}</th>
                                    <th>{t("picking_wave_picker_col")}</th>
                                    <th>{t("picking_wave_remaining_col")}</th>
                                    <th>{t("picking_admin_orders_col")}</th>
                                    <th>{t("picking_wave_started_col")}</th>
                                </tr>
                            </thead>
                            <tbody>{waves.map(renderRow)}</tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Detail modal */}
            {selectedWaveId !== null && (
                <div
                    onClick={closeDetail}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100,
                    }}
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: "var(--color-bg, white)", borderRadius: 8, padding: 24,
                            maxWidth: 960, maxHeight: "85vh", overflowY: "auto", width: "90%",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                            <h2 style={{ flex: 1, margin: 0 }}>{t("picking_wave_id_col")} #{selectedWaveId}</h2>
                            {/* Force-release: superAdmin only, while the wave
                                is still live state (active/paused). For
                                'building' status (admin-generated wave never
                                claimed) it's also available — releases the
                                empty-allocation wave cleanly. */}
                            {isSuperAdmin && detail
                                && (detail.wave.status === "active" || detail.wave.status === "paused" || detail.wave.status === "building") && (
                                <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() => forceRelease(detail.wave.wave_id)}
                                    disabled={releasing || readOnly}
                                    aria-disabled={readOnly}
                                    title={readOnly ? t("read_only_mode") : undefined}
                                >
                                    {t("picking_admin_force_release_btn")}
                                </button>
                            )}
                            <button type="button" className="btn btn-secondary btn-sm" onClick={closeDetail}>
                                {t("close")}
                            </button>
                        </div>
                        {detailLoading || !detail ? (
                            <div className="admin-settings-empty">{t("loading")}</div>
                        ) : (
                            <>
                                <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "4px 16px", marginBottom: 16 }}>
                                    <strong>{t("warehouse_col")}</strong><span>{detail.wave.warehouse}</span>
                                    <strong>{t("picking_admin_pick_type_col")}</strong><span>{t(waveTypeLabelKey(detail.wave.pick_type, detail.wave.sort_station))}</span>
                                    {(detail.wave.segment_count ?? 1) > 1 && (
                                        <>
                                            <strong>{t("picking_joined_label")}</strong>
                                            <span>
                                                {t("picking_segment_badge", { no: String(detail.wave.segment_no ?? 1), of: String(detail.wave.segment_count) })}
                                                {detail.wave.sort_group_id != null && ` · ${t("picking_joined_group", { wave: String(detail.wave.sort_group_id) })}`}
                                            </span>
                                        </>
                                    )}
                                    <strong>{t("picking_tier")}</strong><span>{t(detail.wave.tier === "high" ? "picking_tier_high" : "picking_tier_low")}</span>
                                    <strong>{t("picking_admin_status_col")}</strong><span>{t(`picking_admin_status_${detail.wave.status}`)}</span>
                                    <strong>{t("picking_alloc_method")}</strong><span>{t(`picking_alloc_${detail.wave.alloc_method}`)}</span>
                                    <strong>{t("picking_wave_picker_col")}</strong><span>{detail.wave.current_picker_name ?? "—"}</span>
                                    <strong>{t("picking_wave_started_col")}</strong><span>{formatTs(detail.wave.started_at)}</span>
                                </div>

                                <h3>{t("picking_admin_orders_col")} ({detail.orders.length})</h3>
                                <ul style={{ marginBottom: 16 }}>
                                    {detail.orders.map(o => (
                                        <li key={`${o.order_id}-${o.tier}`}>
                                            {o.order_id} · {t(o.tier === "high" ? "picking_tier_high" : "picking_tier_low")}
                                            {o.is_anchor && <strong> · {t("picking_admin_anchor")}</strong>} · {o.status}
                                        </li>
                                    ))}
                                </ul>

                                <h3>{t("picking_admin_lines_col")} ({detail.lines.length})</h3>
                                <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 16 }}>
                                    <thead>
                                        <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                                            <th>#</th>
                                            <th>{t("picking_admin_location_col")}</th>
                                            <th>{t("picking_admin_walk_order_col")}</th>
                                            <th>SKU</th>
                                            <th>{t("picking_admin_qty_col")}</th>
                                            <th>{t("picking_admin_status_col")}</th>
                                            <th>{t("picking_admin_distribution_col")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.lines.map(l => (
                                            <tr key={l.line_id}>
                                                <td>{l.sort_index}</td>
                                                <td>{l.location}</td>
                                                <td>{l.walk_order}</td>
                                                <td>{l.sku}</td>
                                                <td>{l.qty_picked}/{l.qty_required}</td>
                                                <td>{l.status}</td>
                                                <td style={{ fontSize: "0.85em" }}>
                                                    {l.distribution.map(d => `${d.order_id}×${d.qty}`).join(", ")}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                <h3>{t("picking_admin_events_col")} ({detail.events.length})</h3>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr style={{ textAlign: "left", borderBottom: "1px solid var(--color-border)" }}>
                                            <th>{t("picking_admin_when_col")}</th>
                                            <th>{t("picking_admin_action_col")}</th>
                                            <th>{t("picking_wave_picker_col")}</th>
                                            <th>SKU</th>
                                            <th>{t("picking_admin_location_col")}</th>
                                            <th>Tote</th>
                                            <th>{t("picking_admin_qty_col")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {detail.events.map(e => (
                                            <tr key={e.event_id}>
                                                <td>{formatTs(e.ts)}</td>
                                                <td>{e.action_type}</td>
                                                <td>{e.picker_name ?? "—"}</td>
                                                <td>{e.sku ?? "—"}</td>
                                                <td>{e.location ?? "—"}</td>
                                                <td>{e.tote_id ?? "—"}</td>
                                                <td>{e.qty ?? "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default PickingAdmin;
